import { signInWithEmailAndPassword, signOut, User } from 'firebase/auth';
import { auth, db } from '../../../firebase/config';
import { doc, getDoc, Timestamp, collection, collectionGroup, getDocs, query, where, limit } from 'firebase/firestore';

export async function signInWithEmail(email: string, password: string): Promise<User> {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    return cred.user;
}

export async function signOutUser(): Promise<void> {
    await signOut(auth);
}

export type BankDoc = {
    userId: string;
    companyId: string;
    companyName: string;
    companyNo: string;
    companyAddress: string;
    email: string;
    imgUrl?: string;
    createdAt?: number;
}

export type PsoDoc = {
    authUid: string;
    name: string;
    email: string;
    contact?: string;
    designation?: string;
    createdAt?: number;
}

function serializeFirestore<T>(input: any): T {
    if (input instanceof Timestamp) {
        return input.toMillis() as unknown as T;
    }
    if (Array.isArray(input)) {
        return input.map((v) => serializeFirestore(v)) as unknown as T;
    }
    if (input && typeof input === 'object') {
        const out: Record<string, any> = {};
        for (const [k, v] of Object.entries(input)) out[k] = serializeFirestore(v);
        return out as unknown as T;
    }
    return input as T;
}

export async function fetchBankAndPso(bankId: string, psoId: string): Promise<{ bank: BankDoc; pso: PsoDoc }> {
    const bankRef = doc(db, 'banks', bankId);
    const bankSnap = await getDoc(bankRef);
    if (!bankSnap.exists()) throw new Error('Bank not found');

    const psoRef = doc(db, 'banks', bankId, 'pos', psoId);
    const psoSnap = await getDoc(psoRef);
    if (!psoSnap.exists()) throw new Error('PSO not found');

    const bank = serializeFirestore<BankDoc>(bankSnap.data());
    const pso = serializeFirestore<PsoDoc>(psoSnap.data());
    return { bank, pso };
}

export async function findBankAndPsoByAuthUid(uid: string): Promise<{ bankId: string; psoId: string; bank: BankDoc; pso: PsoDoc }> {
    const q = query(collectionGroup(db, 'pos'), where('authUid', '==', uid), limit(1));
    const snaps = await getDocs(q);
    if (snaps.empty) throw new Error('PSO_NOT_FOUND');
    const psoSnap = snaps.docs[0];
    const pso = serializeFirestore<PsoDoc>(psoSnap.data());
    const psoId = psoSnap.id;
    const bankRef = psoSnap.ref.parent.parent;
    if (!bankRef) throw new Error('BANK_REF_MISSING');
    const bankSnap = await getDoc(bankRef);
    if (!bankSnap.exists()) throw new Error('BANK_NOT_FOUND');
    const bank = serializeFirestore<BankDoc>(bankSnap.data());
    return { bankId: bankRef.id, psoId, bank, pso };
}

export async function findBankAndPsoByAuthUidWithFallback(uid: string): Promise<{ bankId: string; psoId: string; bank: BankDoc; pso: PsoDoc }> {
    try {
        return await findBankAndPsoByAuthUid(uid);
    } catch (err) {
        // Fallback: iterate banks and look in each 'pos'
        const banksSnap = await getDocs(collection(db, 'banks'));
        for (const bankDoc of banksSnap.docs) {
            const bankId = bankDoc.id;
            // Query by field
            const innerQ = query(collection(db, 'banks', bankId, 'pos'), where('authUid', '==', uid), limit(1));
            const inner = await getDocs(innerQ);
            if (!inner.empty) {
                const psoSnap = inner.docs[0];
                const pso = serializeFirestore<PsoDoc>(psoSnap.data());
                const bank = serializeFirestore<BankDoc>(bankDoc.data());
                return { bankId, psoId: psoSnap.id, bank, pso };
            }
            // Try by document id
            const direct = await getDoc(doc(db, 'banks', bankId, 'pos', uid));
            if (direct.exists()) {
                const pso = serializeFirestore<PsoDoc>(direct.data());
                const bank = serializeFirestore<BankDoc>(bankDoc.data());
                return { bankId, psoId: direct.id, bank, pso };
            }
        }
        throw new Error('PSO_NOT_FOUND');
    }
}

export async function findBankByAuthUid(uid: string): Promise<{ bankId: string; bank: BankDoc } | null> {
    // Try query by userId field
    const q = query(collection(db, 'banks'), where('userId', '==', uid), limit(1));
    const snaps = await getDocs(q);
    if (!snaps.empty) {
        const d = snaps.docs[0];
        return { bankId: d.id, bank: serializeFirestore<BankDoc>(d.data()) };
    }
    // Try direct doc id equals uid
    const direct = await getDoc(doc(db, 'banks', uid));
    if (direct.exists()) {
        return { bankId: direct.id, bank: serializeFirestore<BankDoc>(direct.data()) };
    }
    return null;
}




